import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Login from "./components/Login";
import Callback from "./components/Callback";
import SpaceProfile from "./components/SpaceProfile";
import SpaceDashboard from "./components/SpaceDashboard";
import CompanyDashboard from "./components/CompanyDashboard";
import MandantCases from "./components/MandantCases";

import AdminLogin from "./components/AdminLogin";
import AdminCallback from "./components/AdminCallback";
import AdminDashboard from "./components/AdminDashboard";

// ========== HELPERS ==========
const readCompanyContext = () => {
  try {
    return JSON.parse(localStorage.getItem("company_context") || "{}");
  } catch {
    return {};
  }
};

const getCompanyId = () => {
  const direct = String(localStorage.getItem("company_id") || "").trim();
  if (direct) return direct;

  const ctx = readCompanyContext();
  return String(ctx.company_id || "").trim();
};

const getProfileCompleted = () => {
  if (localStorage.getItem("company_profile_completed") === "true") {
    return true;
  }

  const ctx = readCompanyContext();

  if (ctx?.completed === true) {
    return true;
  }

  const hasCompanyId = String(ctx?.company_id || "").trim().length > 0;
  const hasLegalName =
    String(ctx?.legal_name || ctx?.company_name || "").trim().length > 0;

  return hasCompanyId && hasLegalName;
};

// ========== ROOT ROUTE ==========
const RootRoute = () => {
  const companyId = getCompanyId();
  const profileCompleted = getProfileCompleted();

  if (!companyId) {
    return <Login />;
  }

  if (profileCompleted) {
    return <Navigate to="/company-dashboard" replace />;
  }

  return <Navigate to="/profile" replace />;
};

// ========== PROFILE GATE ==========
const ProfileGate = ({ children }) => {
  const companyId = getCompanyId();
  const profileCompleted = getProfileCompleted();

  const spaceToken = localStorage.getItem("space_token");
  const spaceId = localStorage.getItem("space_id");
  const providerType = localStorage.getItem("provider_type");
  const continueWithoutProvider = localStorage.getItem(
    "continue_without_provider"
  );

  const hasCobotConnection = !!spaceToken && !!spaceId;
  const hasProviderContext = !!providerType;
  const hasManualContinue = continueWithoutProvider === "true";

  if (companyId && profileCompleted) {
    return <Navigate to="/company-dashboard" replace />;
  }

  if (
    !companyId &&
    !hasCobotConnection &&
    !hasProviderContext &&
    !hasManualContinue
  ) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// ========== SPACE GATE (ALT COBOT) ==========
const SpaceGate = ({ children }) => {
  const spaceToken = localStorage.getItem("space_token");
  const spaceId = localStorage.getItem("space_id");

  if (!spaceToken || !spaceId) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// ========== COMPANY GATE ==========
const CompanyGate = ({ children }) => {
  const companyId = getCompanyId();
  const profileCompleted = getProfileCompleted();

  if (!companyId) {
    return <Navigate to="/" replace />;
  }

  if (!profileCompleted) {
    return <Navigate to="/profile" replace />;
  }

  return children;
};

// ========== ADMIN GATE ==========
const AdminGate = ({ children }) => {
  const adminIdToken = localStorage.getItem("admin_id_token");
  const adminAccessToken = localStorage.getItem("admin_access_token");

  if (!adminIdToken || !adminAccessToken) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

export default function App() {
  return (
    <>
      <div className="bg-yellow-200 text-black p-4 text-center font-bold text-xl">
        ✅ Inkasso Plattform – Mandant & Admin
      </div>

      <BrowserRouter>
        <Routes>
          {/* ===== ROOT ===== */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/callback" element={<Callback />} />

          {/* ===== PROFILE ===== */}
          <Route
            path="/profile"
            element={
              <ProfileGate>
                <SpaceProfile />
              </ProfileGate>
            }
          />
          <Route
            path="/profile/"
            element={
              <ProfileGate>
                <SpaceProfile />
              </ProfileGate>
            }
          />

          {/* ===== ALT COBOT DASHBOARD ===== */}
          <Route
            path="/dashboard"
            element={
              <SpaceGate>
                <SpaceDashboard />
              </SpaceGate>
            }
          />

          {/* ===== COMPANY DASHBOARD ===== */}
          <Route
            path="/company-dashboard"
            element={
              <CompanyGate>
                <CompanyDashboard />
              </CompanyGate>
            }
          />

          {/* ===== INKASSO AKTEN ===== */}
          <Route
            path="/inkasso/akten"
            element={
              <CompanyGate>
                <MandantCases />
              </CompanyGate>
            }
          />

          {/* ===== ADMIN ===== */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/callback" element={<AdminCallback />} />

          <Route
            path="/admin"
            element={
              <AdminGate>
                <Navigate to="/admin/dashboard" replace />
              </AdminGate>
            }
          />

          <Route
            path="/admin/dashboard"
            element={
              <AdminGate>
                <AdminDashboard />
              </AdminGate>
            }
          />

          {/* ===== FALLBACK ===== */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}